import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";

import { CapacityStatus, ServiceDetailType, ServiceReserveStatus } from "@/apis/clients";
import { CheckboxCardType } from "@/components/atoms/inputForms/CheckboxCardType";
import { RadioCardType } from "@/components/atoms/inputForms/RadioCardType";
import { Schedule } from "@/states/page-editor/template-parts/service";
import { MenuTemplateType } from "@/states/page-editor/template-parts/service-menu";
import { useHandleFieldValueChange } from "@/states/service-form/operations";
import {
  ConfirmServiceInfo,
  isBeforeTimeLimit,
  useConfirmInputList,
} from "@/states/service-form/selectors";
import { ServiceFormServiceInputKey, ServiceInfo } from "@/states/service-form/types";
import { format } from "@/utils/date";
import { formatNumber } from "@/utils/number";
import { HOURS_MINUTES_FORMAT, JP_FULL_DATE_TIME_FORMAT } from "@/utils/const";
import { Select } from "@/components/atoms/inputForms/Select";

interface ScheduleViewItemsProps {
  schedules: Schedule[];
  reserveStatus: ServiceReserveStatus;
  type: MenuTemplateType;
  isConfirm?: boolean;
  isViewSeatsLeft?: boolean;
  disablePast?: boolean;
  service: ServiceInfo | undefined
}

export function ScheduleViewItems({
  schedules,
  reserveStatus,
  type,
  isConfirm = false,
  isViewSeatsLeft,
  disablePast,
  service,
}: ScheduleViewItemsProps) {
  const confirmInputList = useConfirmInputList();

  const onFieldValueChange =
    useHandleFieldValueChange<ServiceFormServiceInputKey>();
  const handleDisableInput = (
    item: Schedule | ConfirmServiceInfo["details"][0],
  ) => {
    const curerrtDate = new Date();
    if (
      item.start &&
      !isBeforeTimeLimit(item.start, curerrtDate) &&
      type === "menu.eventSeminar"
    ) {
      return true;
    }
    return item?.remainingReservationCount === 0;
  };

  const [optionIdsSelected, setOptionIdsSelected] = useState<number[]>([]);
  const [quantity, setQuantity] = useState<{ [key: string]: number }>({});

  const DEFAULT_QUANTITY = 1;

  useEffect(() => {
    const _quantity = isConfirm ? (confirmInputList.serviceDraft?.quantity || {}) : (confirmInputList.service?.quantity || {});

    if (Object.keys(_quantity).length > 0) {
      setQuantity(_quantity);
      onFieldValueChange("quantity", _quantity);
      return;
    }

    if (schedules && schedules.length > 0) {
      const _quantity = Object.fromEntries(
        schedules.map(item => [item.id, item.maxTicketsPerOrder ? DEFAULT_QUANTITY : 0])
      );
      setQuantity(_quantity);
      onFieldValueChange("quantity", _quantity);
    }
  }, [confirmInputList.serviceDraft?.quantity, confirmInputList.service?.quantity, isConfirm, onFieldValueChange, schedules]);

  const detailIds = isConfirm
    ? confirmInputList.serviceDraft?.details
      ?.filter((detail) => !handleDisableInput(detail))
      .map((item) => item.id) || []
    : confirmInputList.service?.details
      ?.filter((detail) => !handleDisableInput(detail))
      .map((item) => item.id) || [];

  useEffect(() => {
    if (schedules.length === 1) {
      if (schedules[0].remainingReservationCount !== 0) {
        onFieldValueChange("detailIds", [Number(schedules[0].id)]);
      }
    }
    if (
      reserveStatus === ServiceReserveStatus.ALL &&
      type === "menu.eventSeminar"
    ) {
      const activeSchedule = schedules.filter(
        (item) => item.remainingReservationCount !== 0,
      );
      const ids = activeSchedule.map((item) => Number(item.id));
      onFieldValueChange("detailIds", ids);
    }
  }, [onFieldValueChange, reserveStatus, schedules, type]);
  const containerClass = (item: Schedule) =>
    !(
      optionIdsSelected.includes(Number(item.id)) ||
      detailIds.includes(Number(item.id)) ||
      handleDisableInput(item)
    )
      ? "bg-white"
      : "";

    const isSetCapacityAll =
      service?.type === "event-seminar" &&
      service.capacity_status === CapacityStatus.ALL;
    
    const isDisabledAll = useMemo(() => {
      if (!isSetCapacityAll) return false;
    
      const hasRemaining = typeof service?.remaining_capacity === "number";
      const noCapacity =
        service?.type === "event-seminar" && service.remaining_capacity === 0;
    
      return (
        (reserveStatus === ServiceReserveStatus.ALL &&
          hasRemaining &&
          schedules.length > (service?.remaining_capacity ?? 0)) || noCapacity
      );
    }, [isSetCapacityAll, schedules, service, reserveStatus]);
  
    const checkIsShowWarning = ({ scheduleId }: { scheduleId: string }): boolean => {
      if (!isSetCapacityAll || typeof service?.remaining_capacity !== "number") {
        return false;
      }
    
      const cap = service.remaining_capacity;
      const getTotal = (ids: number[]) => ids.reduce((sum, id) => sum + (quantity[id] ?? 0), 0);
    
      switch (reserveStatus) {
        case ServiceReserveStatus.SINGLE_SELECT:
          return (
            (optionIdsSelected.includes(Number(scheduleId)) ||
              detailIds.includes(Number(scheduleId))) &&
            quantity[scheduleId] > cap
          );
    
        case ServiceReserveStatus.MULTIPLE_SELECT: {
          const selectedIds = [...optionIdsSelected, ...detailIds];
          return selectedIds.includes(Number(scheduleId)) && getTotal(selectedIds) > cap;
        }
    
        default: {
          const allIds = schedules.map((item) => Number(item.id));
          return getTotal(allIds) > cap;
        }
      }
    };

    useEffect(() => {
      const selectContainers = document.querySelectorAll(".select-container");
      if (selectContainers.length) {
        selectContainers.forEach((parent) => {
          const boxShowQuantity = parent.querySelector(".box-show-quantity");
          const selectQuantity = parent.querySelector(".select-quantity") as HTMLElement;
  
          if (boxShowQuantity && selectQuantity) {
            const parentRect = parent.getBoundingClientRect();
            const childRect = boxShowQuantity.getBoundingClientRect();
  
            const distanceTop = childRect.top - parentRect.top;
  
            selectQuantity.style.top = `${distanceTop}px`;
          }
        });
      }
    }, []);
  return (
    <div className="space-y-4">
      {schedules
        ?.filter((item) => {
          if (disablePast) {
            return item.end && item.end >= new Date();
          } else {
            return item;
          }
        })
        .map((item, index) => {
          const isDisabledItem = handleDisableInput(item);
          const isShowQuantity = type === "menu.eventSeminar" && !!item.maxTicketsPerOrder && item.maxTicketsPerOrder > 1;
          const maxQuantity = item.remainingReservationCount !== null ? Math.min(item.maxTicketsPerOrder ?? 1, item.remainingReservationCount ?? 1) : (item.maxTicketsPerOrder ?? 1);
          
          return (
            <div key={index}>
              {reserveStatus === ServiceReserveStatus.SINGLE_SELECT &&
                schedules.length > 1 && (
                  <div className="select-container relative">
                    <RadioCardType
                      id={`detailIds-${item.id}`}
                      name="detailIds"
                      value={item.id}
                      label={
                        <ItemContent
                          isShowPrice={true}
                          partType={type}
                          item={item}
                          isViewSeatsLeft={isViewSeatsLeft}
                          isShowQuantity={isShowQuantity}
                          showWarning={checkIsShowWarning({
                            scheduleId: item.id
                          })}
                          isDisabledAll={isDisabledAll}
                        />
                      }
                      labelClassName="w-full"
                      containerClassName={clsx(
                        "w-full !items-start -mt-1 !py-[10px] !px-[6px] !opacity-100",
                        containerClass(item),
                        {
                          "all-boxShowQuantity-color-gray": isDisabledItem || isDisabledAll,
                        },
                      )}
                      radioClassName=""
                      onChange={(value) => {
                        setOptionIdsSelected([Number(value)]);
                        onFieldValueChange("detailIds", [Number(value)]);
                      }}
                      checked={
                        optionIdsSelected.includes(Number(item.id)) ||
                        detailIds.includes(Number(item.id))
                      }
                      isDisabled={isDisabledItem || isDisabledAll}
                    /> 
                    {
                      isShowQuantity && (
                        <div className="select-quantity absolute right-[13px] w-[109px] h-11 cursor-pointer">
                          <Quantity
                            isDisabled={isDisabledItem || isDisabledAll}
                            maxQuantity={maxQuantity}
                            quantity={quantity[item.id]}
                            onChangeQuantity={(value) => {
                              const _quantity = { ...quantity, [item.id]: value };
                              onFieldValueChange("quantity", _quantity);
                              setQuantity(_quantity);
                            }}
                          />
                        </div>
                      )
                    }
                  </div>

                )}
              {reserveStatus === ServiceReserveStatus.MULTIPLE_SELECT &&
                schedules.length > 1 && (
                  <div className="select-container relative">
                    <CheckboxCardType
                      id={`detailIds-${item.id}`}
                      value={item.id}
                      onChange={(value) => {
                        let detailId = [...detailIds];
                        if (detailId.includes(Number(value))) {
                          detailId = detailIds.filter(
                            (id) => id !== Number(value),
                          );
                        } else {
                          detailId.push(Number(value));
                        }
                        onFieldValueChange("detailIds", detailId);
                      }}
                      onClick={() => {
                        return;
                      }}
                      label={
                        <ItemContent
                          isShowPrice={true}
                          classNames=""
                          partType={type}
                          item={item}
                          isViewSeatsLeft={isViewSeatsLeft}
                          isShowQuantity={isShowQuantity}
                          showWarning={checkIsShowWarning({
                            scheduleId: item.id
                          })}
                          isDisabledAll={isDisabledAll}
                        />
                      }
                      containerClassName={clsx(
                        "w-full bg-white !items-start [>.input-checkbox]:!mt-1 !p-[12px] !opacity-100",
                        containerClass(item),
                        {
                          "all-boxShowQuantity-color-gray": isDisabledItem,
                        },
                      )}
                      checked={
                        optionIdsSelected.includes(Number(item.id)) ||
                        detailIds.includes(Number(item.id))
                      }
                      disabled={isDisabledItem || isDisabledAll}
                    />
                    {
                      isShowQuantity && (
                        <div className="select-quantity absolute right-[13px] w-[109px] h-11  cursor-pointer">
                          <Quantity
                            isDisabled={isDisabledItem || isDisabledAll}
                            maxQuantity={maxQuantity}
                            quantity={quantity[item.id]}
                            onChangeQuantity={(value) => {
                              const _quantity = { ...quantity, [item.id]: value };
                              onFieldValueChange("quantity", _quantity);
                              setQuantity(_quantity);
                            }}
                          />
                        </div>
                      )
                    }
                  </div>
                )}
              {(reserveStatus === ServiceReserveStatus.ALL ||
                schedules.length === 1) && (
                  <div className="select-container relative">
                    <div
                      className={clsx(
                        "flex items-center bg-white border rounded-[4px] px-[14px] py-[11px] border-[#DFDFE2] w-full !p-[12px] relative",
                        {
                          "hover:border-[#DFDFE2] bg-[#F7F7F7] opacity-80":
                            handleDisableInput(item) || isDisabledAll,
                        },
                      )}
                    >
                      <ItemContent
                        isShowPrice={
                          !(
                            type === "menu.eventSeminar" &&
                            reserveStatus === ServiceReserveStatus.ALL &&
                            index > 0
                          )
                        }
                        partType={type}
                        item={item}
                        isViewSeatsLeft={isViewSeatsLeft}
                        isShowQuantity={isShowQuantity}
                        showWarning={checkIsShowWarning({
                          scheduleId: item.id
                        })}
                        isDisabledAll={isDisabledAll}
                      />
                      {
                        isShowQuantity && (
                          <div className={`select-quantity absolute right-[12px] w-[109px] h-11 cursor-pointer`}>
                            <Quantity
                              isDisabled={isDisabledItem || isDisabledAll}
                              maxQuantity={maxQuantity}
                              quantity={quantity[item.id]}
                              onChangeQuantity={(value) => {
                                const _quantity = { ...quantity, [item.id]: value };
                                onFieldValueChange("quantity", _quantity);
                                setQuantity(_quantity);
                              }}
                            />
                          </div>
                        )
                      }
                    </div>
                  </div>
                )}
            </div>
          );
        })}
    </div>
  );
}

interface ItemContentProps {
  item: Schedule;
  partType: MenuTemplateType;
  isShowPrice?: boolean;
  classNames?: string;
  isViewSeatsLeft?: boolean;
  isShowQuantity?: boolean;
  showWarning?: boolean;
  isDisabledAll?: boolean;
}
const ItemContent = ({
  item,
  partType,
  isShowPrice,
  classNames = "",
  isViewSeatsLeft,
  isShowQuantity,
  showWarning = false,
  isDisabledAll,
}: ItemContentProps) => {
  const {
    name = "",
    type,
    start,
    end,
    description,
    maximumNumber,
    remainingReservationCount,
    placeName,
    price,
    isShowFree
  } = item;

  const status =
    type === ServiceDetailType.OFFLINE ? placeName : "オンライン開催";
  const isSoldOut = (maximumNumber && remainingReservationCount === 0) || isDisabledAll;

  return (
    <div className="w-full ">
      <div className="w-full flex">
        <div className="flex-1">
          <p
            className={`font-bold text-gray-900 text-[16px] mb-2 break-word  leading-5 ${classNames}`}
          >
            {name}
          </p>
          {partType === "menu.eventSeminar" && (
            <p className="mb-2 text-[14px]">
              {`${start ? format(start, JP_FULL_DATE_TIME_FORMAT) : ""} 〜 ${
                end ? format(end, HOURS_MINUTES_FORMAT) : ""
              }`}
              {!!start && !isBeforeTimeLimit(start) ? "  終了しました" : ""}
            </p>
          )}

          {partType === "menu.eventSeminar" && (
            <p className="mb-2 text-[14px]">{status}</p>
          )}
        </div>
        {isSoldOut && (
          <div className="border-[4px] border-[#D32932] border-solid rounded-[6px] !text-[#D32932] h-[56px] w-[68px] flex items-center justify-center text-[24px] font-bold">
            満席
          </div>
        )}
      </div>

      {isShowPrice && (
        <p className="w-full text-right text-[14px] mb-2">
          {maximumNumber && remainingReservationCount !== undefined && (
            <>
              {isViewSeatsLeft && (
                <span>
                  {`残り${formatNumber(remainingReservationCount) ?? ""}名/`}
                </span>
              )}
              <span>{`定員${formatNumber(maximumNumber) ?? ""}名`}</span>
            </>
          )}
          {price && price > 0 ? (
            <span className="font-bold text-[16px] ml-2">{`${formatNumber(
              price,
            )}円 (税込)`}</span>
          ) : isShowFree ? (
            <span className="font-bold text-[16px] ml-2">無料</span>
          ) : null}
        </p>
      )}

      {/* Placeholder for the select box component Quantity */}
      {isShowQuantity && (
        <div className="box-show-quantity h-11 flex items-center gap-2 justify-end mb-3">
          <p className="text-[14px]">チケット数</p>
          <div className="w-[109px] h-11 " />
        </div>
      )}

      {description && (
        <div className="w-full border-t py-2 text-[14px] break-all">
          <p className="break-word">{description}</p>
        </div>
      )}

      {/* {showWarning && 
        <div className="py-2 px-4 border border-solid border-[#FFE58F] bg-[#FFFBE6] text-xs rounded-lg flex gap-2">
          <CausionCircle className="w-4 h-4 shrink-0" />
          定員の残数に対して、チケット数が上限を超えました。お手数ですがチケット数を変更して申込みしてください。
        </div>
      } */}
    </div>
  );
};

// Quantity
const Quantity = ({
  quantity,
  onChangeQuantity,
  maxQuantity,
  isDisabled,
}: {
  quantity: number;
  maxQuantity: number;
  onChangeQuantity?: (value: number) => void;
  isDisabled?: boolean;
}) => {


  return (
    <Select
      options={Array.from({ length: maxQuantity }, (_, i) => ({
        text: `${i + 1}枚`,
        value: i + 1,
      }))}
      onChange={(value) => {
        onChangeQuantity?.(Number(value));
      }}
      placeholder="00"
      value={quantity}
      className="max-w-[109px] w-full"
      isDisabled={isDisabled}
    />
  );
};
